import React, { useState, useEffect } from 'react';
import { MapPin, Plane, Hotel, Shield, Ship, Car, Heart, Users, ChevronRight, MessageCircle, Send, Check, Headphones, Globe, CreditCard, Star, Route } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Plane, Hotel, MapPin, Shield, Ship, Car, Heart, Users,
  Headphones, Globe, CreditCard, Star, Route
};

const DEFAULT_SERVICES = [
  { icon: 'Plane',    title: 'Passagens Aéreas',     desc: 'Nacionais e internacionais com as melhores tarifas' },
  { icon: 'Hotel',    title: 'Hospedagens',          desc: 'Hotéis, pousadas e resorts selecionados' },
  { icon: 'MapPin',   title: 'Passeios',             desc: 'Experiências e atividades no destino' },
  { icon: 'Shield',   title: 'Seguro Viagem',        desc: 'Proteção completa para sua tranquilidade' },
  { icon: 'Ship',     title: 'Cruzeiros',            desc: 'As melhores rotas marítimas' },
  { icon: 'Car',      title: 'Locação de Veículos',  desc: 'Liberdade para explorar no seu ritmo' },
  { icon: 'Heart',    title: 'Lua de Mel',           desc: 'Roteiros românticos inesquecíveis' },
  { icon: 'Users',    title: 'Grupos Privados',      desc: 'Viagens exclusivas para seu grupo' },
];

const DEFAULT_BENEFITS = [
  { icon: 'Headphones',  title: 'Atendimento Humanizado',  desc: 'Consultores dedicados do início ao fim' },
  { icon: 'Globe',       title: 'Nacional e Internacional', desc: 'Destinos no Brasil e no mundo' },
  { icon: 'Users',       title: 'Experiência em Grupos',    desc: 'Anos organizando viagens em grupo' },
  { icon: 'CreditCard',  title: 'Pagamento Flexível',       desc: 'Parcelamento facilitado' },
  { icon: 'Star',        title: 'Suporte Real',             desc: 'Acompanhamento antes, durante e depois' },
  { icon: 'Route',       title: 'Roteiros Exclusivos',      desc: 'Experiências personalizadas e únicas' },
];

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&q=80';

const CustomPackages = () => {
  const [content, setContent] = useState({
    hero_image: DEFAULT_HERO_IMAGE,
    hero_title: 'Monte sua viagem do seu jeito.',
    hero_subtitle: 'Criamos roteiros personalizados nacionais e internacionais, adaptados ao seu perfil, orçamento e estilo de viagem.',
    services: DEFAULT_SERVICES,
    benefits: DEFAULT_BENEFITS,
    form_title: 'Solicite sua cotação',
    form_subtitle: 'Preencha abaixo e nossa equipe entra em contato em até 24h.',
    cta_title: 'Pronto para começar?',
    cta_subtitle: 'Fale com nossa equipe e transforme seu sonho de viagem em realidade.',
    whatsapp_number: '5521999999999',
  });
  const [formData, setFormData] = useState({
    name: '', whatsapp: '', email: '', destination: '',
    people: '', date: '', trip_type: '', budget: '', details: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/packages/site-settings`)
      .then(r => r.json())
      .then(d => {
        if (d?.custom_packages_content) {
          setContent(prev => ({ ...prev, ...d.custom_packages_content }));
        }
      })
      .catch(() => {});
  }, []);

  // Resolve image URL (relative path → backend full URL)
  const resolveImage = (path) => {
    if (!path) return DEFAULT_HERO_IMAGE;
    if (path.startsWith('http') || path.startsWith('data:')) return path;
    if (path.startsWith('/')) return `${BACKEND_URL}${path}`;
    return path;
  };

  // Map a stored item ({icon: "Plane", title, desc}) to render-ready ({IconComponent, title, desc})
  const resolveItem = (item, fallback) => {
    if (!item) return null;
    const iconName = item.icon || fallback?.icon;
    const IconComponent = ICON_MAP[iconName] || ICON_MAP[fallback?.icon] || Star;
    return { IconComponent, title: item.title || '', desc: item.desc || '' };
  };

  const heroImage = resolveImage(content.hero_image);
  const services = (content.services && content.services.length > 0 ? content.services : DEFAULT_SERVICES)
    .map((s, i) => resolveItem(s, DEFAULT_SERVICES[i]));
  const benefits = (content.benefits && content.benefits.length > 0 ? content.benefits : DEFAULT_BENEFITS)
    .map((b, i) => resolveItem(b, DEFAULT_BENEFITS[i]));
  const whatsappNumber = content.whatsapp_number || '5521999999999';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.whatsapp) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/lead-forms/responses`, {
        form_id: 'custom-packages-page',
        answers: Object.entries(formData).filter(([, v]) => v).map(([k, v]) => ({ question_id: k, answer: v }))
      });
      setSent(true);
    } catch {}
    finally { setSending(false); }
  };

  const openWhatsApp = () => {
    const msg = encodeURIComponent('Olá! Vim pela página de Pacotes Personalizados e gostaria de montar minha viagem.');
    window.open(`https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${msg}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section
        className="relative h-[70vh] sm:h-[80vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}
        data-testid="custom-packages-hero"
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-testid="custom-packages-hero-title">
            {content.hero_title}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl mx-auto" data-testid="custom-packages-hero-subtitle">
            {content.hero_subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#formulario"
              className="btn-primary-theme font-bold px-8 py-4 rounded-xl text-lg shadow-lg inline-flex items-center gap-2"
            >
              Solicitar Cotação <ChevronRight className="w-5 h-5" />
            </a>
            <button
              onClick={openWhatsApp}
              className="bg-[#25D366] hover:brightness-110 text-white font-bold px-8 py-4 rounded-xl text-lg shadow-lg inline-flex items-center gap-2 transition-all"
            >
              <MessageCircle className="w-5 h-5" /> Falar no WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* O QUE PERSONALIZAMOS */}
      <section className="py-16 sm:py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-theme-primary">
            O que personalizamos para você
          </h2>
          <p className="text-center text-gray-500 mb-12 max-w-xl mx-auto">
            Cada detalhe da sua viagem, planejado com carinho e expertise.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {services.map((s, i) => (
              <Card key={i} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-0 shadow-sm">
                <CardContent className="p-5 sm:p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-theme-light flex items-center justify-center mx-auto mb-3">
                    <s.IconComponent className="w-6 h-6 text-theme-primary" />
                  </div>
                  <h3 className="font-bold text-sm sm:text-base text-gray-900 mb-1">{s.title}</h3>
                  <p className="text-xs text-gray-500 hidden sm:block">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FORMULÁRIO */}
      <section id="formulario" className="py-16 sm:py-20 px-6 bg-theme-light">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 text-theme-primary" data-testid="custom-packages-form-title">
            {content.form_title}
          </h2>
          <p className="text-center text-gray-500 mb-10" data-testid="custom-packages-form-subtitle">
            {content.form_subtitle}
          </p>

          {sent ? (
            <Card className="text-center py-12">
              <CardContent>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Cotação enviada!</h3>
                <p className="text-gray-500 mb-6">Em breve nossa equipe entrará em contato.</p>
                <button onClick={openWhatsApp} className="bg-[#25D366] text-white font-bold px-6 py-3 rounded-xl inline-flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Falar agora no WhatsApp
                </button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome *</Label>
                      <Input required value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Seu nome completo" className="mt-1" />
                    </div>
                    <div>
                      <Label>WhatsApp *</Label>
                      <Input required value={formData.whatsapp} onChange={(e) => setFormData(p => ({ ...p, whatsapp: e.target.value }))} placeholder="(21) 99999-9999" className="mt-1" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="seu@email.com" className="mt-1" />
                    </div>
                    <div>
                      <Label>Destino desejado</Label>
                      <Input value={formData.destination} onChange={(e) => setFormData(p => ({ ...p, destination: e.target.value }))} placeholder="Ex: Maldivas, Fernando de Noronha..." className="mt-1" />
                    </div>
                    <div>
                      <Label>Quantidade de pessoas</Label>
                      <Input type="number" min="1" value={formData.people} onChange={(e) => setFormData(p => ({ ...p, people: e.target.value }))} placeholder="2" className="mt-1" />
                    </div>
                    <div>
                      <Label>Data aproximada</Label>
                      <Input type="date" value={formData.date} onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label>Tipo de viagem</Label>
                      <Select value={formData.trip_type} onValueChange={(v) => setFormData(p => ({ ...p, trip_type: v }))}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lazer">Lazer</SelectItem>
                          <SelectItem value="lua-de-mel">Lua de Mel</SelectItem>
                          <SelectItem value="familia">Família</SelectItem>
                          <SelectItem value="aventura">Aventura</SelectItem>
                          <SelectItem value="grupo">Grupo</SelectItem>
                          <SelectItem value="corporativo">Corporativo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Faixa de orçamento</Label>
                      <Select value={formData.budget} onValueChange={(v) => setFormData(p => ({ ...p, budget: v }))}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ate-3k">Até R$ 3.000/pessoa</SelectItem>
                          <SelectItem value="3k-5k">R$ 3.000 - R$ 5.000/pessoa</SelectItem>
                          <SelectItem value="5k-10k">R$ 5.000 - R$ 10.000/pessoa</SelectItem>
                          <SelectItem value="10k-20k">R$ 10.000 - R$ 20.000/pessoa</SelectItem>
                          <SelectItem value="acima-20k">Acima de R$ 20.000/pessoa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Detalhes adicionais</Label>
                    <textarea
                      rows={3}
                      value={formData.details}
                      onChange={(e) => setFormData(p => ({ ...p, details: e.target.value }))}
                      placeholder="Conte mais sobre o que você imagina para essa viagem..."
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-1"
                      style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                    />
                  </div>
                  <Button type="submit" disabled={sending} className="btn-primary-theme w-full text-lg py-6 rounded-xl font-bold">
                    {sending ? 'Enviando...' : 'Solicitar minha cotação'}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* CREDIBILIDADE */}
      <section className="py-16 sm:py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-theme-primary">
            Por que viajar com a TÔ INDO ALI?
          </h2>
          <p className="text-center text-gray-500 mb-12 max-w-xl mx-auto">
            Mais do que uma agência, somos seus parceiros de aventura.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <div className="w-10 h-10 rounded-lg bg-theme-light flex items-center justify-center flex-shrink-0">
                  <b.IconComponent className="w-5 h-5 text-theme-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{b.title}</h3>
                  <p className="text-sm text-gray-500">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-12 px-6 bg-theme-primary">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-testid="custom-packages-cta-title">
            {content.cta_title}
          </h2>
          <p className="text-white/70 mb-8" data-testid="custom-packages-cta-subtitle">
            {content.cta_subtitle}
          </p>
          <button
            onClick={openWhatsApp}
            className="bg-[#25D366] hover:brightness-110 text-white font-bold px-10 py-5 rounded-xl text-lg shadow-xl inline-flex items-center gap-3 transition-all"
          >
            <MessageCircle className="w-6 h-6" /> Falar no WhatsApp
          </button>
        </div>
      </section>
    </div>
  );
};

export default CustomPackages;
